import type { ComponentProps, FormEvent, KeyboardEvent } from 'react';
import { LiksConfirmDialog } from '../../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import type {
  CrawlJob,
  CrawlSchedule,
  CrawlUrlNode,
  PendingKnowledgeDelete,
  WithResourceId,
} from '../../../types/knowledge';
import { CrawlScheduleModal } from '../CrawlScheduleModal';
import { CrawlUrlSelectionModal } from '../CrawlUrlSelectionModal';
import { StartCrawlModal } from '../StartCrawlModal';

type KnowledgeCrawlsDialogsProps = {
  translate: (key: string, params?: Record<string, string>) => string;
  selectionCrawl: WithResourceId<CrawlJob> | null;
  selectionNodes: Array<WithResourceId<CrawlUrlNode>>;
  selectedNodeIds: string[];
  setSelectedNodeIds: (ids: string[]) => void;
  confirmSelection: () => void | Promise<void>;
  handleCloseSelection: () => void;
  isAwaitingSelectionStart: boolean;
  isScheduledAwaitingSelection: boolean;
  isSelectionSubmitting: boolean;
  isSelectionLoading: boolean;
  isStartCrawlModalOpen: boolean;
  crawlUrl: string;
  crawlMaxDepth: string;
  crawlMaxPages: string;
  crawlUseStartUrlAsPrefixFilter: boolean;
  crawlAllowSubdomains: boolean;
  crawlExcludedUrls: string[];
  setCrawlUrl: (value: string) => void;
  setCrawlMaxDepth: (value: string) => void;
  setCrawlMaxPages: (value: string) => void;
  onCrawlMaxDepthBlur: () => void;
  onCrawlMaxPagesBlur: () => void;
  setCrawlUseStartUrlAsPrefixFilter: (value: boolean) => void;
  setCrawlAllowSubdomains: (value: boolean) => void;
  setCrawlExcludedUrls: (value: string[]) => void;
  handleStartCrawlSubmit: (event: FormEvent<HTMLFormElement>) => void;
  handleCloseStartCrawlModal: () => void;
  isStartingCrawl: boolean;
  preventInvalidNumericKey: (event: KeyboardEvent<HTMLInputElement>) => void;
  scheduleModalCrawl: WithResourceId<CrawlJob> | null;
  scheduleModalExisting: WithResourceId<CrawlSchedule> | null | undefined;
  handleSaveSchedule: ComponentProps<typeof CrawlScheduleModal>['onSave'];
  setPendingScheduleDelete: (
    schedule: WithResourceId<CrawlSchedule> | null,
  ) => void;
  handleCloseScheduleModal: () => void;
  isScheduleSubmitting: boolean;
  pendingScheduleDelete: WithResourceId<CrawlSchedule> | null;
  handleDeleteSchedule: () => void | Promise<void>;
  pendingDelete: PendingKnowledgeDelete | null;
  setPendingDelete: (value: PendingKnowledgeDelete | null) => void;
  confirmDeleteCrawl: () => void | Promise<void>;
  isDeletingCrawl: boolean;
};

export function KnowledgeCrawlsDialogs(props: KnowledgeCrawlsDialogsProps) {
  const {
    translate,
    selectionCrawl,
    selectionNodes,
    selectedNodeIds,
    setSelectedNodeIds,
    confirmSelection,
    handleCloseSelection,
    isAwaitingSelectionStart,
    isScheduledAwaitingSelection,
    isSelectionSubmitting,
    isSelectionLoading,
    isStartCrawlModalOpen,
    crawlUrl,
    crawlMaxDepth,
    crawlMaxPages,
    crawlUseStartUrlAsPrefixFilter,
    crawlAllowSubdomains,
    crawlExcludedUrls,
    setCrawlUrl,
    setCrawlMaxDepth,
    setCrawlMaxPages,
    onCrawlMaxDepthBlur,
    onCrawlMaxPagesBlur,
    setCrawlUseStartUrlAsPrefixFilter,
    setCrawlAllowSubdomains,
    setCrawlExcludedUrls,
    handleStartCrawlSubmit,
    handleCloseStartCrawlModal,
    isStartingCrawl,
    preventInvalidNumericKey,
    scheduleModalCrawl,
    scheduleModalExisting,
    handleSaveSchedule,
    setPendingScheduleDelete,
    handleCloseScheduleModal,
    isScheduleSubmitting,
    pendingScheduleDelete,
    handleDeleteSchedule,
    pendingDelete,
    setPendingDelete,
    confirmDeleteCrawl,
    isDeletingCrawl,
  } = props;

  return (
    <>
      {selectionCrawl && (
        <CrawlUrlSelectionModal
          startUrl={selectionCrawl.startUrl ?? ''}
          nodes={selectionNodes}
          selectedNodeIds={selectedNodeIds}
          onSelectedNodeIdsChange={setSelectedNodeIds}
          onConfirm={() => void confirmSelection()}
          onClose={handleCloseSelection}
          confirmLabel={
            isAwaitingSelectionStart
              ? translate('knowledge.crawlSelectionContinue')
              : translate('knowledge.crawlSelectionSave')
          }
          description={
            isAwaitingSelectionStart
              ? translate('knowledge.crawlSelectionHintAwaiting')
              : isScheduledAwaitingSelection
                ? translate('knowledge.crawlSelectionHintScheduledAwaiting')
                : selectionCrawl.status === 'CRAWLING' ||
                    selectionCrawl.status === 'PROCESSING'
                  ? translate('knowledge.crawlSelectionHintInProgress')
                  : translate('knowledge.crawlSelectionHintIdle')
          }
          isSubmitting={isSelectionSubmitting}
          isLoading={isSelectionLoading}
        />
      )}

      {isStartCrawlModalOpen ? (
        <StartCrawlModal
          crawlUrl={crawlUrl}
          crawlMaxDepth={crawlMaxDepth}
          crawlMaxPages={crawlMaxPages}
          crawlUseStartUrlAsPrefixFilter={crawlUseStartUrlAsPrefixFilter}
          crawlAllowSubdomains={crawlAllowSubdomains}
          crawlExcludedUrls={crawlExcludedUrls}
          onCrawlUrlChange={setCrawlUrl}
          onCrawlMaxDepthChange={setCrawlMaxDepth}
          onCrawlMaxPagesChange={setCrawlMaxPages}
          onCrawlMaxDepthBlur={onCrawlMaxDepthBlur}
          onCrawlMaxPagesBlur={onCrawlMaxPagesBlur}
          onCrawlUseStartUrlAsPrefixFilterChange={
            setCrawlUseStartUrlAsPrefixFilter
          }
          onCrawlAllowSubdomainsChange={setCrawlAllowSubdomains}
          onCrawlExcludedUrlsChange={setCrawlExcludedUrls}
          onSubmit={handleStartCrawlSubmit}
          onClose={handleCloseStartCrawlModal}
          isSubmitting={isStartingCrawl}
          preventInvalidNumericKey={preventInvalidNumericKey}
        />
      ) : null}

      {scheduleModalCrawl && (
        <CrawlScheduleModal
          crawlStartUrl={scheduleModalCrawl.startUrl ?? ''}
          schedule={scheduleModalExisting ?? null}
          onSave={handleSaveSchedule}
          onDelete={
            scheduleModalExisting
              ? async () => {
                  setPendingScheduleDelete(scheduleModalExisting);
                }
              : undefined
          }
          onClose={handleCloseScheduleModal}
          isSubmitting={isScheduleSubmitting}
        />
      )}

      <LiksConfirmDialog
        isOpen={pendingScheduleDelete !== null}
        title={translate('knowledge.crawlScheduleDeleteTitle')}
        message={translate('knowledge.crawlScheduleDeleteMessage')}
        confirmLabel={
          isScheduleSubmitting
            ? translate('common.deleting')
            : translate('knowledge.deleteConfirm')
        }
        cancelLabel={translate('knowledge.deleteCancel')}
        isBusy={isScheduleSubmitting}
        onConfirm={() => void handleDeleteSchedule()}
        onCancel={() => {
          if (!isScheduleSubmitting) {
            setPendingScheduleDelete(null);
          }
        }}
      />

      <LiksConfirmDialog
        isOpen={pendingDelete?.type === 'crawl'}
        title={translate('knowledge.deleteCrawlTitle')}
        message={translate(
          pendingDelete?.scheduleId
            ? 'knowledge.deleteCrawlMessageWithSchedule'
            : 'knowledge.deleteCrawlMessage',
          {
            url: pendingDelete?.label ?? pendingDelete?.id ?? '',
            documentCount: String(pendingDelete?.documentCount ?? 0),
          },
        )}
        confirmLabel={
          isDeletingCrawl
            ? translate('common.deleting')
            : translate('knowledge.deleteConfirm')
        }
        cancelLabel={translate('knowledge.deleteCancel')}
        isBusy={isDeletingCrawl}
        onConfirm={() => void confirmDeleteCrawl()}
        onCancel={() => {
          if (!isDeletingCrawl) {
            setPendingDelete(null);
          }
        }}
      />
    </>
  );
}
